import React, { useEffect, useState } from "react";
import ProposalTimeline from "./ProposalTimeline";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase-config";

const ProposalPage = () => {
    const { projectId } = useParams();
    const [proposal, setProposal] = useState(null);
    const [projectName, setProjectName] = useState("");
    const [stages, setStages] = useState([]);
    const [projectData, setProjectData] = useState(null)

    useEffect(() => {
        const fetchProposal = async () => {
            try {
                const projectRef = doc(db, "projects", projectId);
                const projectSnap = await getDoc(projectRef);

                if (projectSnap.exists()) {
                    const projectData = projectSnap.data();
                    setProjectData(projectData); // Set project data for future use
                    setProposal(projectData.proposalDocuments.proposal);
                    setProjectName(projectData.projectName || "Unnamed Project");
                    setStages(projectData.stages || []); // Set stages from project data
                } else {
                    console.error("Project not found.");
                }
            } catch (error) {
                console.error("Error fetching proposal:", error);
            }
        };

        fetchProposal();
    }, [projectId]);

    if (!proposal || !stages.length) {
        return (
            <div style={{minHeight:'100vh',background:'var(--surface-200)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)'}}>Loading proposal...</p>
            </div>
        );
    }

    const proposalDocuments = projectData?.proposalDocuments || {};
    const formatLink = (value) => {
        if (!value) return "";
        return value.startsWith("http") ? value : `https://${value}`;
    };
    const contractLink = formatLink(proposalDocuments.contract);
    const invoiceLink = formatLink(proposalDocuments.invoice);
    const renderListSection = (title, items) => {
        if (!items?.length) return null;

        return (
            <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 16px'}}>{title}</h2>
                <ul style={{margin:0,paddingLeft:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
                    {items.map((item, i) => (
                        <li key={i} style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.6}}>{item}</li>
                    ))}
                </ul>
            </section>
        );
    };

    return (
        <div style={{minHeight:'100vh',background:'var(--surface-200)',padding:'64px 24px 96px'}}>
            <div style={{maxWidth:'760px',margin:'0 auto'}}>

                {/* Header */}
                <div style={{marginBottom:'48px',paddingBottom:'32px',borderBottom:'1px solid var(--border)'}}>
                    <p style={{fontFamily:'var(--font-mono)',fontSize:'0.6875rem',textTransform:'uppercase',letterSpacing:'0.1em',color:'var(--text-tertiary)',margin:'0 0 12px'}}>Next Level Websites — Proposal</p>
                    <h1 style={{fontFamily:'var(--font-display)',fontSize:'clamp(2rem,5vw,3.5rem)',fontWeight:700,letterSpacing:'-2px',color:'var(--text-primary)',margin:'0 0 8px',lineHeight:1.05}}>{projectName}</h1>
                    <p style={{fontFamily:'var(--font-body)',fontSize:'1rem',color:'var(--text-secondary)',fontStyle:'italic',margin:0}}>{proposal.service || "Website Project"}</p>
                </div>

                {/* Summary */}
                {proposal.summary && (
                    <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                        <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 12px'}}>Project Summary</h2>
                        <p style={{fontFamily:'var(--font-body)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.75,margin:0}}>{proposal.summary}</p>
                    </section>
                )}

                {/* Goals */}
                <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                    <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 12px'}}>Goals for this Project</h2>
                    <p style={{fontFamily:'var(--font-body)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.75,margin:0}}>{proposal.goals || "Final project goals will be confirmed before work begins."}</p>
                </section>

                {/* Investment */}
                <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                    <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 12px'}}>Investment</h2>
                    <p style={{fontFamily:'var(--font-display)',fontSize:'2.5rem',fontWeight:700,letterSpacing:'-1px',color:'var(--accent)',margin:0,lineHeight:1}}>${proposal.investment}</p>
                </section>

                {renderListSection("Not Included", proposal.notIncluded)}
                {renderListSection("Payment Schedule", proposal.paymentSchedule)}

                {proposal.carePlanOption && (
                    <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                        <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 12px'}}>{proposal.carePlanOption.title}</h2>
                        <p style={{fontFamily:'var(--font-display)',fontSize:'1.75rem',fontWeight:700,letterSpacing:'-0.5px',color:'var(--accent)',margin:'0 0 10px',lineHeight:1}}>{proposal.carePlanOption.price}</p>
                        <p style={{fontFamily:'var(--font-body)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.75,margin:0}}>{proposal.carePlanOption.description}</p>
                    </section>
                )}

                {/* Service details */}
                <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                    <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 20px'}}>{proposal.service}</h2>
                    {Object.entries(proposal.serviceDetails || {}).map(([section, items]) => (
                        <div key={section} style={{marginBottom:'20px'}}>
                            <p style={{fontFamily:'var(--font-mono)',fontSize:'0.6875rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-tertiary)',margin:'0 0 8px'}}>{section.replace(/([A-Z])/g,' $1').trim().replace(/^./,s=>s.toUpperCase())}</p>
                            <ul style={{margin:0,paddingLeft:'16px',display:'flex',flexDirection:'column',gap:'6px'}}>
                                {items.map((item,i) => <li key={i} style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.6}}>{item}</li>)}
                            </ul>
                        </div>
                    ))}
                </section>

                {/* Timeline */}
                <section style={{marginBottom:'32px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                    <ProposalTimeline projectId={projectId} />
                </section>

                {/* Next Steps */}
                <section style={{marginBottom:'48px',padding:'28px 32px',background:'var(--surface-100)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                    <h2 style={{fontFamily:'var(--font-display)',fontSize:'1.125rem',fontWeight:700,letterSpacing:'-0.3px',color:'var(--text-primary)',margin:'0 0 12px'}}>Next Steps</h2>
                    <p style={{fontFamily:'var(--font-body)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.75,margin:'0 0 16px'}}>{proposal.nextSteps}</p>
                    <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
                        {contractLink ? (
                        <a href={contractLink} target="_blank" rel="noopener noreferrer"
                            style={{display:'inline-block',background:'var(--accent)',color:'#fff',borderRadius:'var(--radius-sm)',padding:'11px 24px',fontFamily:'var(--font-ui)',fontSize:'0.9375rem',fontWeight:600,textDecoration:'none'}}>
                            Sign the Contract →
                        </a>
                        ) : null}
                        {invoiceLink ? (
                        <a href={invoiceLink} target="_blank" rel="noopener noreferrer"
                            style={{display:'inline-block',background:'transparent',color:'var(--text-primary)',border:'1px solid var(--border-medium)',borderRadius:'var(--radius-sm)',padding:'11px 24px',fontFamily:'var(--font-ui)',fontSize:'0.9375rem',fontWeight:600,textDecoration:'none'}}>
                            Pay the Deposit →
                        </a>
                        ) : null}
                        {!contractLink && !invoiceLink ? (
                            <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-tertiary)',margin:0}}>Contract and invoice links will be added before approval.</p>
                        ) : null}
                    </div>
                </section>

                {/* Footer */}
                <p style={{fontFamily:'var(--font-body)',fontSize:'0.875rem',color:'var(--text-tertiary)',textAlign:'center',fontStyle:'italic'}}>We look forward to working with you.</p>
            </div>
        </div>
    );
};

export default ProposalPage;
